import { useState } from "react";
import { 
  ChevronDown, 
  ChevronRight, 
  Fuel, 
  Database, 
  FileText, 
  Activity, 
  Cpu, 
  Thermometer, 
  BarChart, 
  Target, 
  Zap, 
  Clock, 
  Settings,
  Filter,
  Hash
} from "lucide-react";

const AEFuelConsumption = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const dataSources = [
    {
      collection: "VesselAuxEngineData",
      fields: [
        "vesselId", "voyageId", "engineId", "recordedAt", "engineType",
        "fuelID", "fuelType", "sulphurContent", "fuelConsumption",
        "powerOutput", "maxCapacityLoad", "reportedCapacityLoad", 
        "averageReportedLoad", "rpm", "powerLoad", "sfoc", "status"
      ],
      purpose: "Auxiliary engine operational and fuel data"
    },
    {
      collection: "Vessel",
      fields: ["name", "imoNumber", "vesselCode"],
      purpose: "Vessel identification and metadata"
    },
    {
      collection: "VesselVoyage",
      fields: ["voyageId", "departTime", "arrivalTime"],
      purpose: "Voyage information and routing"
    },
    {
      collection: "VesselPerformance",
      fields: ["runningBdnId", "aeFuelTypes", "voyageId", "createdAt"],
      purpose: "Performance records and fuel type tracking"
    }
  ];

  const coreCalculations = [
    {
      id: "fuel-type-extraction",
      title: "Fuel Type Extraction",
      description: "Extracts fuel types from multiple sources",
      formula: "Fuel Type = From VesselAuxEngineData OR VesselPerformance",
      subFormulas: [
        "Primary: fuelType array from VesselAuxEngineData",
        "Secondary: aeFuelTypes from VesselPerformance",
        "Array processing with deduplication",
        "Fallback: 'HFO' if no data found"
      ],
      icon: <Fuel className="w-5 h-5" />,
      borderColor: "border-purple-200",
      bgColor: "bg-purple-50",
      textColor: "text-purple-700",
    },
    {
      id: "fuel-consumption-aggregation",
      title: "Fuel Consumption Aggregation",
      description: "Aggregates fuel consumption by type and total",
      formula: "Total Fuel = HFO + LFO + MGO + Biofuel",
      subFormulas: [
        "HFO: Heavy Fuel Oil consumption",
        "LFO: Light Fuel Oil consumption",
        "MGO: Marine Gas Oil consumption",
        "Biofuel: Alternative fuel consumption",
        "Auto-calculated total via Mongoose pre-save hook"
      ],
      icon: <Thermometer className="w-5 h-5" />,
      borderColor: "border-amber-200",
      bgColor: "bg-amber-50",
      textColor: "text-amber-700",
    },
    {
      id: "sulfur-content-processing",
      title: "Sulfur Content Processing",
      description: "Processes sulfur content data with defaults",
      formula: "Sulfur Content = Recorded value OR default (0.44%)",
      subFormulas: [
        "Direct from sulphurContent field",
        "Default value: 0.5% per schema",
        "Display format: `${value} %`",
        "Quality metric for emissions calculation"
      ],
      icon: <Hash className="w-5 h-5" />,
      borderColor: "border-red-200",
      bgColor: "bg-red-50",
      textColor: "text-red-700",
    },
    {
      id: "running-bdn-tracking",
      title: "Running BDN ID Tracking",
      description: "Tracks bunker delivery note ID across records",
      formula: "runningBdnId = Latest VesselPerformance.runningBdnId",
      subFormulas: [
        "From most recent VesselPerformance record",
        "Used to group related fuel type records",
        "Essential for fuel type consistency",
        "Null if no performance records found"
      ],
      icon: <FileText className="w-5 h-5" />,
      borderColor: "border-teal-200",
      bgColor: "bg-teal-50",
      textColor: "text-teal-700",
    }
  ];

  const queryModes = [
    {
      mode: "Voyage Mode",
      condition: "voyageId provided in query",
      process: [
        "Find VesselVoyage by _id",
        "Extract voyageId",
        "Query VesselAuxEngineData by voyageId",
        "Get VesselPerformance records for same voyage"
      ],
      icon: <Target className="w-5 h-5" />,
      borderColor: "border-indigo-200",
      bgColor: "bg-indigo-50",
      textColor: "text-indigo-700",
    },
    {
      mode: "Date Range Mode",
      condition: "No voyageId provided",
      process: [
        "Calculate date range from query params",
        "Default: last 4 days",
        "Query VesselAuxEngineData by date range",
        "Get latest VesselPerformance in date range"
      ],
      icon: <Clock className="w-5 h-5" />,
      borderColor: "border-violet-200",
      bgColor: "bg-violet-50",
      textColor: "text-violet-700",
    }
  ];

  const dataProcessing = [
    {
      id: "fuel-type-array-handling",
      title: "Fuel Type Array Handling",
      description: "Processes fuel type data from multiple formats",
      values: [
        "Array from VesselAuxEngineData.fuelType",
        "Array from VesselPerformance.aeFuelTypes",
        "Flat mapping and deduplication",
        "AE index matching: AE1 → index 0, AE2 → index 1"
      ],
      icon: <Filter className="w-5 h-5" />,
      borderColor: "border-pink-200",
      bgColor: "bg-pink-50",
      textColor: "text-pink-700",
    },
    {
      id: "engine-index-mapping",
      title: "Engine Index Mapping",
      description: "Maps engine ID to array index for fuel type matching",
      values: [
        "Regex extraction: /AE(\\d+)/i",
        "AE1 → index 0, AE2 → index 1, AE3 → index 2",
        "Fallback to index 0 if no match",
        "Used to select correct fuel type from array"
      ],
      icon: <Cpu className="w-5 h-5" />,
      borderColor: "border-emerald-200",
      bgColor: "bg-emerald-50",
      textColor: "text-emerald-700",
    },
    {
      id: "data-quality-checks",
      title: "Data Quality Checks",
      description: "Validates data completeness and consistency",
      values: [
        "Check for empty fuel type arrays",
        "Validate sulphur content values",
        "Verify engine ID existence",
        "Handle null/missing performance records"
      ],
      icon: <Settings className="w-5 h-5" />,
      borderColor: "border-orange-200",
      bgColor: "bg-orange-50",
      textColor: "text-orange-700",
    }
  ];

  const schemaDefaults = {
    "maxCapacityLoad": "550 kW (default)",
    "sulphurContent": "0.5% (default)",
    "fuelConsumption.hfo": "0 MT (default)",
    "fuelConsumption.lfo": "0 MT (default)",
    "fuelConsumption.mgo": "0 MT (default)",
    "fuelConsumption.biofuel": "0 MT (default)",
    "status": "'sailing' (default enum)"
  };

  const calculationProcess = [
    { step: 1, title: "Parameter Validation", description: "Parse vesselId, engineId, dates, voyageId" },
    { step: 2, title: "Mode Selection", description: "Determine voyage mode or date range mode" },
    { step: 3, title: "Date Range Calculation", description: "Calculate analysis period based on mode" },
    { step: 4, title: "Engine Data Query", description: "Fetch VesselAuxEngineData with regex matching" },
    { step: 5, title: "Performance Data Query", description: "Get VesselPerformance for fuel types and BDN" },
    { step: 6, title: "Fuel Type Processing", description: "Extract and process fuel type information" },
    { step: 7, title: "Data Aggregation", description: "Aggregate consumption and quality metrics" },
    { step: 8, title: "Response Compilation", description: "Package results with metadata" },
  ];

  const apiEndpoint = {
    method: "GET",
    endpoint: "/api/vessels/:vesselId/aux-engines/:engineId/fuel-consumption",
    parameters: "vesselId, engineId, startDate, endDate, days, voyageId",
    description: "Retrieve auxiliary engine fuel consumption data with quality metrics"
  };

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#b6faed] rounded-lg">
              <Fuel className="w-6 h-6 text-[#117864]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                Auxiliary Engine Fuel Calculations
              </h2>
              <p className="text-gray-600 text-sm">
                Fuel consumption analysis, quality assessment, and performance tracking
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200 space-y-8">
            {/* Data Sources */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Data Sources & Collections</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {dataSources.map((source, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold text-gray-800">{source.collection}</h4>
                    </div>
                    <p className="text-sm text-gray-700 mb-3">{source.purpose}</p>
                    <div className="text-xs text-gray-600 space-y-1">
                      <div className="font-medium mb-1">Key Fields:</div>
                      {source.fields.map((field, idx) => (
                        <div key={idx} className="font-mono truncate">• {field}</div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Core Calculations */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Core Calculations & Metrics</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 gap-6">
                {coreCalculations.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex items-start gap-3 mb-4">
                      <div className={`p-2 rounded-lg ${calc.textColor} bg-white`}>
                        {calc.icon}
                      </div>
                      <div className="flex-1">
                        <h4 className={`font-bold text-gray-800 mb-1 ${calc.textColor}`}>
                          {calc.title}
                        </h4>
                        <p className="text-sm text-gray-600">{calc.description}</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Formula/Logic:</p>
                        <div className="text-sm bg-white p-3 rounded border border-gray-200 font-mono">
                          {calc.formula}
                        </div>
                      </div>
                      
                      {calc.subFormulas && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Details:</p>
                          <div className="space-y-2">
                            {calc.subFormulas.map((subFormula, idx) => (
                              <div key={idx} className="text-sm bg-white p-2 rounded border border-gray-100">
                                {subFormula}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default AEFuelConsumption;